/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	AccountRecoveryPasswordStrengthEvaluationRequestDTO,
	AccountRecoveryService,
	ApiError,
	EnterPasswordForAccountRecoveryDTO,
	EnterPhoneVerificationCodeForAccountRecoveryDTO,
	StartAccountRecoveryDTO
} from '../../generated/iam-client';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
	iamApiAccountRecoveryEnterPasswordNotFound,
	iamApiAccountRecoveryEnterPasswordUnexpectedError,
	iamApiAccountRecoveryEnterPasswordValidationException,
	iamApiAccountRecoveryEnterPhoneVerificationCodeNotFound,
	iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError,
	iamApiAccountRecoveryEnterPhoneVerificationCodeValidationException,
	iamApiAccountRecoveryEvaluatePasswordStrengthNotFound,
	iamApiAccountRecoveryEvaluatePasswordStrengthUnexpectedError,
	iamApiAccountRecoveryPasswordEntered,
	iamApiAccountRecoveryPasswordStrengthEvaluated,
	iamApiAccountRecoveryPhoneVerificationCodeEntered,
	iamApiAccountRecoveryRestartPhoneVerificationNotFound,
	iamApiAccountRecoveryRestartPhoneVerificationRequested,
	iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError,
	iamApiAccountRecoveryRetryPhoneVerificationNotFound,
	iamApiAccountRecoveryRetryPhoneVerificationRequested,
	iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError,
	iamApiAccountRecoveryStarted,
	iamApiAccountRecoveryStateRetrieved,
	iamApiRetrieveAccountRecoveryStateNotFound,
	iamApiRetrieveAccountRecoveryStateUnexpectedError,
	iamApiStartAccountRecoveryUnexpectedError,
	iamApiStartAccountRecoveryValidationException
} from './actions';
import {AnyAction} from 'redux';

class AccountRecoveryResource {

	startAccountRecovery(command: StartAccountRecoveryDTO): Observable<AnyAction> {
		const promise = AccountRecoveryService.startAccountRecovery(command);
		return from(promise)
			.pipe(
				map(() => iamApiAccountRecoveryStarted()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 422:
							return of(iamApiStartAccountRecoveryValidationException(error.body));
						default:
							return of(iamApiStartAccountRecoveryUnexpectedError());
					}

				})
			);
	}

	accountRecoveryState(accountRecoveryId: string): Observable<AnyAction> {
		const promise = AccountRecoveryService.accountRecoveryState(accountRecoveryId);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiAccountRecoveryStateRetrieved({state: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiRetrieveAccountRecoveryStateNotFound());
						default:
							return of(iamApiRetrieveAccountRecoveryStateUnexpectedError());
					}
				})
			);
	}

	retryPhoneVerification(accountRecoveryId: string): Observable<AnyAction> {
		const promise = AccountRecoveryService.retryPhoneVerificationForAccountRecovery(accountRecoveryId);
		return from(promise)
			.pipe(
				map(() => iamApiAccountRecoveryRetryPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAccountRecoveryRetryPhoneVerificationNotFound());
						default:
							return of(iamApiAccountRecoveryRetryPhoneVerificationUnexpectedError());

					}
				})
			);
	}

	restartPhoneVerification(accountRecoveryId: string): Observable<AnyAction> {
		const promise = AccountRecoveryService.restartPhoneVerificationForAccountRecovery(accountRecoveryId);
		return from(promise)
			.pipe(
				map(() => iamApiAccountRecoveryRestartPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAccountRecoveryRestartPhoneVerificationNotFound());
						default:
							return of(iamApiAccountRecoveryRestartPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	enterPhoneVerificationCode(accountRecoveryId: string, command: EnterPhoneVerificationCodeForAccountRecoveryDTO): Observable<AnyAction> {
		const promise = AccountRecoveryService.enterPhoneVerificationCodeForAccountRecovery(accountRecoveryId, command);
		return from(promise)
			.pipe(
				map(() => iamApiAccountRecoveryPhoneVerificationCodeEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAccountRecoveryEnterPhoneVerificationCodeNotFound());
						case 422:
							return of(iamApiAccountRecoveryEnterPhoneVerificationCodeValidationException(error.body));
						default:
							return of(iamApiAccountRecoveryEnterPhoneVerificationCodeUnexpectedError());
					}
				})
			);
	}

	evaluatedPasswordStrength(
		accountRecoveryId: string, evaluationRequest: AccountRecoveryPasswordStrengthEvaluationRequestDTO
	): Observable<AnyAction> {
		const promise = AccountRecoveryService.evaluatedPasswordStrengthForAccountRecovery(accountRecoveryId, evaluationRequest);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiAccountRecoveryPasswordStrengthEvaluated({passwordStrength: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAccountRecoveryEvaluatePasswordStrengthNotFound());
						default:
							return of(iamApiAccountRecoveryEvaluatePasswordStrengthUnexpectedError());
					}
				})
			);
	}

	enterPassword(accountRecoveryId: string, command: EnterPasswordForAccountRecoveryDTO): Observable<AnyAction> {
		const promise = AccountRecoveryService.enterPasswordForAccountRecovery(accountRecoveryId, command);
		return from(promise)
			.pipe(
				map(() => iamApiAccountRecoveryPasswordEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAccountRecoveryEnterPasswordNotFound());
						case 422:
							return of(iamApiAccountRecoveryEnterPasswordValidationException(error.body));
						default:
							return of(iamApiAccountRecoveryEnterPasswordUnexpectedError());
					}
				})
			);
	}
}

export default AccountRecoveryResource;
